import { cn } from "@/lib/utils";
import type { EmulatorSessionDTO } from "@mm-sales/shared";
import { type CSSProperties, useEffect, useMemo, useState } from "react";

/**
 * What the demo POS printed. The demo build's virtual printer records every print call
 * (text with alignment/size/bold, columns, feeds, logos, barcodes) and the broker relays
 * finished printouts per slot at /print/<slot>/ (proxied by the api). `usePrintFeed` keeps
 * the jobs of one live device; `Paper` draws one printout the way an 80 mm thermal printer
 * lays it out: 48 columns at font size 24, everything else scaled from that.
 */
export type PrintOp =
  | {
      t: "text";
      s: string;
      a?: number;
      f?: number;
      b?: boolean;
      u?: boolean;
      dw?: boolean;
      dh?: boolean;
      inv?: boolean;
    }
  | { t: "cols"; c: string[]; w: number[]; al: number[]; a?: number; f?: number; b?: boolean }
  | { t: "wrap"; n: number }
  | { t: "img"; png: string; w: number; h: number; a?: number }
  | { t: "bar"; d: string; a?: number }
  | { t: "qr"; d: string; a?: number }
  | { t: "cut" };

export type PrintJob = {
  id: string;
  /** "kitchen" or "receipt" (anything else comes out of the receipt printer). */
  kind: string;
  ts: number;
  ops: PrintOp[];
  /** Which live device printed it (added client-side). */
  slot: string;
  purpose?: string | null;
};

type Msg =
  | { type: "snapshot"; jobs: Omit<PrintJob, "slot">[] }
  | { type: "job"; job: Omit<PrintJob, "slot"> };

export function usePrintFeed(session: EmulatorSessionDTO | null): PrintJob[] {
  const slot = session?.slot ?? null;
  const purpose = session?.purpose ?? null;
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  useEffect(() => {
    setJobs([]);
    if (!slot) return;
    let stopped = false;
    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const tag = (j: Omit<PrintJob, "slot">): PrintJob => ({ ...j, slot, purpose });
    const connect = () => {
      if (stopped) return;
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${proto}//${window.location.host}/print/${slot}/`);
      ws.onmessage = (e) => {
        let msg: Msg;
        try {
          msg = JSON.parse(String(e.data)) as Msg;
        } catch {
          return;
        }
        if (msg.type === "snapshot") setJobs(msg.jobs.map(tag));
        else if (msg.type === "job")
          setJobs((prev) =>
            prev.some((p) => p.id === msg.job.id) ? prev : [...prev, tag(msg.job)],
          );
      };
      ws.onclose = () => {
        if (stopped) return;
        retry = setTimeout(connect, 2500);
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      ws?.close();
    };
  }, [slot, purpose]);
  return jobs;
}

/** Jobs of several live devices, oldest first (every screen on the floor can print). */
export function mergeJobs(...feeds: PrintJob[][]): PrintJob[] {
  return feeds.flat().sort((a, b) => a.ts - b.ts || a.id.localeCompare(b.id));
}

export const isKitchen = (j: PrintJob) => j.kind === "kitchen";

// ---- rendering --------------------------------------------------------------------------

type Seg = {
  s: string;
  f: number;
  b?: boolean;
  u?: boolean;
  dw?: boolean;
  dh?: boolean;
  inv?: boolean;
};
type Block =
  | { k: "line"; segs: Seg[]; a: number }
  | { k: "cols"; op: Extract<PrintOp, { t: "cols" }> }
  | { k: "wrap"; n: number }
  | { k: "img"; op: Extract<PrintOp, { t: "img" }> }
  | { k: "code"; op: Extract<PrintOp, { t: "bar" | "qr" }> };

/** Text arrives as fragments ("Total" then "   $12.00\n"); the printer only breaks on "\n". */
function toBlocks(ops: PrintOp[]): Block[] {
  const out: Block[] = [];
  let cur: Seg[] = [];
  const close = (a: number) => {
    out.push({ k: "line", segs: cur, a });
    cur = [];
  };
  for (const op of ops) {
    if (op.t === "text") {
      const pieces = op.s.split("\n");
      pieces.forEach((piece, i) => {
        if (piece)
          cur.push({
            s: piece,
            f: op.f ?? 24,
            b: op.b,
            u: op.u,
            dw: op.dw,
            dh: op.dh,
            inv: op.inv,
          });
        if (i < pieces.length - 1) close(op.a ?? 0);
      });
      continue;
    }
    if (cur.length) close(0);
    if (op.t === "cols") out.push({ k: "cols", op });
    else if (op.t === "wrap") out.push({ k: "wrap", n: op.n });
    else if (op.t === "img") out.push({ k: "img", op });
    else if (op.t === "bar" || op.t === "qr") out.push({ k: "code", op });
  }
  if (cur.length) close(0);
  return out;
}

const ALIGN = ["left", "center", "right"] as const;
const alignOf = (a: number | undefined) => ALIGN[Math.min(2, Math.max(0, a ?? 0))];
/** Font size in container-query units: size 24 = 48 columns across the paper (monospace glyphs are ~0.6 em; 29.6 leaves a hair of slack so a 48-char divider never wraps). */
const fs = (f: number | undefined, big?: boolean): string =>
  `calc(100cqw * ${(((f ?? 24) / 24) * (big ? 1.7 : 1)) / 29.6})`;
const segStyle = (s: Seg): CSSProperties => ({
  fontSize: fs(s.f, s.dw || s.dh),
  fontWeight: s.b ? 700 : 400,
  textDecoration: s.u ? "underline" : undefined,
  background: s.inv ? "#111318" : undefined,
  color: s.inv ? "#fff" : undefined,
});

export function Paper({ job, className }: { job: PrintJob; className?: string }) {
  const blocks = useMemo(() => toBlocks(job.ops), [job]);
  return (
    <div
      className={cn(
        "w-full whitespace-pre-wrap break-words font-mono leading-[1.25] text-[#111318]",
        className,
      )}
      style={{
        containerType: "inline-size",
        fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        overflowWrap: "anywhere",
      }}
    >
      {blocks.map((b, i) => {
        if (b.k === "line")
          return (
            <div key={i} style={{ textAlign: alignOf(b.a), fontSize: fs(24) }}>
              {b.segs.map((s, j) => (
                <span key={j} style={segStyle(s)}>
                  {s.s}
                </span>
              ))}
              {b.segs.length ? null : " "}
            </div>
          );
        if (b.k === "cols")
          return (
            <div
              key={i}
              className="flex"
              style={{ fontSize: fs(b.op.f), fontWeight: b.op.b ? 700 : 400 }}
            >
              {b.op.c.map((c, j) => (
                <span
                  key={j}
                  style={{
                    flex: `${b.op.w[j] ?? 1} ${b.op.w[j] ?? 1} 0%`,
                    textAlign: alignOf(b.op.al[j]),
                    minWidth: 0,
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          );
        if (b.k === "wrap")
          return <div key={i} style={{ height: `calc(${b.n} * 1.25 * ${fs(24)})` }} />;
        if (b.k === "img")
          return (
            <div
              key={i}
              className="flex"
              style={{
                justifyContent:
                  alignOf(b.op.a) === "left"
                    ? "flex-start"
                    : alignOf(b.op.a) === "right"
                      ? "flex-end"
                      : "center",
              }}
            >
              <img
                src={`data:image/png;base64,${b.op.png}`}
                alt=""
                style={{ width: `${Math.min(100, (b.op.w / 576) * 100)}%`, height: "auto" }}
              />
            </div>
          );
        // barcode / QR: the data, with a code-like mark above it
        const qr = b.op.t === "qr";
        return (
          <div
            key={i}
            className="flex flex-col"
            style={{
              alignItems:
                alignOf(b.op.a) === "left"
                  ? "flex-start"
                  : alignOf(b.op.a) === "right"
                    ? "flex-end"
                    : "center",
              fontSize: fs(20),
            }}
          >
            <div
              style={
                qr
                  ? {
                      width: "34%",
                      aspectRatio: "1",
                      backgroundImage:
                        "repeating-linear-gradient(0deg,#111318 0 12%,transparent 12% 25%),repeating-linear-gradient(90deg,#111318 0 12%,transparent 12% 25%)",
                      backgroundBlendMode: "multiply",
                      border: "2px solid #111318",
                      padding: "4%",
                    }
                  : {
                      width: "70%",
                      height: "2.2em",
                      backgroundImage:
                        "repeating-linear-gradient(90deg,#111318 0 2px,transparent 2px 4px,#111318 4px 7px,transparent 7px 9px,#111318 9px 10px,transparent 10px 13px)",
                    }
              }
            />
            <div className="truncate" style={{ maxWidth: "100%" }}>
              {b.op.d}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const printTime = (ts: number) => {
  const d = new Date(ts);
  return `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() >= 12 ? "PM" : "AM"}`;
};
